import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import {
    ProductCategory,
    Product,
    InventoryDashboardMetrics,
    ManualTransactionDto,
    InventoryTransaction,
    CreateProductDto,
    SaveProcedureRecipeDto,
    Supplier
} from '../models/inventory.models';

@Injectable({
    providedIn: 'root'
})
export class InventoryService {
    private http = inject(HttpClient);
    private apiUrl = `${environment.apiUrl}/inventory`;

    // A helper method to inject the business context, assuming there is a BusinessService or an Interceptor,
    // but in Dentia it is usually done via a header 'x-business-id'.
    // In Dentia Web usually the AuthInterceptor handles appending 'x-business-id',
    // so we don't necessarily have to add it manually here unless required.
    // However, it's good to just make standard HTTP calls here, and the interceptor does the rest.

    // ─── Categories ─────────────────────────────────────────────────────────

    getCategories(): Observable<ProductCategory[]> {
        return this.http.get<ProductCategory[]>(`${this.apiUrl}/categories`);
    }

    createCategory(data: Partial<ProductCategory>): Observable<ProductCategory> {
        return this.http.post<ProductCategory>(`${this.apiUrl}/categories`, data);
    }

    // ─── Products ───────────────────────────────────────────────────────────

    getProducts(params: { page?: number, limit?: number, search?: string, showInactive?: boolean } = {}): Observable<{ data: Product[], total: number }> {
        return this.http.get<{ data: Product[], total: number }>(`${this.apiUrl}/products`, { params: params as any });
    }

    createProduct(data: CreateProductDto): Observable<Product> {
        return this.http.post<Product>(`${this.apiUrl}/products`, data);
    }

    getRecommendedSku(categoryId: string): Observable<{ recommendedSku: string }> {
        return this.http.get<{ recommendedSku: string }>(`${this.apiUrl}/products/recommend-sku`, {
            params: { categoryId }
        });
    }

    getSuppliers(): Observable<Supplier[]> {
        return this.http.get<Supplier[]>(`${this.apiUrl}/suppliers`);
    }

    createSupplier(data: Partial<Supplier>): Observable<Supplier> {
        return this.http.post<Supplier>(`${this.apiUrl}/suppliers`, data);
    }

    updateProduct(id: string, data: Partial<CreateProductDto>): Observable<Product> {
        return this.http.put<Product>(`${this.apiUrl}/products/${id}`, data);
    }

    toggleProductStatus(id: string): Observable<Product> {
        return this.http.patch<Product>(`${this.apiUrl}/products/${id}/toggle-status`, {});
    }

    // ─── Transactions & Dashboard ───────────────────────────────────────────

    getDashboardMetrics(): Observable<InventoryDashboardMetrics> {
        return this.http.get<InventoryDashboardMetrics>(`${this.apiUrl}/dashboard`);
    }

    executeManualTransaction(data: ManualTransactionDto): Observable<InventoryTransaction> {
        return this.http.post<InventoryTransaction>(`${this.apiUrl}/transactions/manual`, data);
    }

    getProductTransactions(productId: string): Observable<InventoryTransaction[]> {
        return this.http.get<InventoryTransaction[]>(`${this.apiUrl}/transactions/product/${productId}`);
    }

    getProductStocks(productId: string): Observable<any[]> {
        return this.http.get<any[]>(`${this.apiUrl}/products/${productId}/stocks`);
    }

    // ─── Recipes ────────────────────────────────────────────────────────────

    getRecipe(procedureId: string): Observable<any[]> {
        return this.http.get<any[]>(`${this.apiUrl}/recipes/${procedureId}`);
    }

    saveRecipe(data: SaveProcedureRecipeDto): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/recipes`, data);
    }
}
